interface ErrorResponse {
  status?: number
  data?: unknown
}

interface ErrorWithResponse {
  response?: ErrorResponse
}

export interface ApiErrorDetails {
  status?: number
  code?: string
}

export const getApiErrorCodeFromValue = (
  value: unknown,
): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown
        return getApiErrorCodeFromValue(parsed)
      } catch {
        // Keep parsing the plain-text error below.
      }
    }

    if (trimmed.toLowerCase() === "no room") return "no_room"

    const code = trimmed
      .split(":", 1)[0]
      ?.trim()
      .toLowerCase()
      .replaceAll("-", "_")

    return code && /^[a-z][a-z0-9_]*$/.test(code) ? code : undefined
  }

  if (typeof value !== "object" || value === null) return undefined

  const data = value as {
    code?: unknown
    error?: unknown
    fields?: unknown
    message?: unknown
  }
  const fieldCode =
    typeof data.fields === "object" && data.fields !== null
      ? Object.values(data.fields)
          .map(getApiErrorCodeFromValue)
          .find((code) => code !== undefined)
      : undefined

  return (
    getApiErrorCodeFromValue(data.code) ??
    fieldCode ??
    getApiErrorCodeFromValue(data.error) ??
    getApiErrorCodeFromValue(data.message)
  )
}

export const getErrorResponse = (error: unknown): ErrorResponse | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined
  }

  return (error as ErrorWithResponse).response
}

export const getApiErrorDetails = (error: unknown): ApiErrorDetails => {
  const response = getErrorResponse(error)
  const directError =
    typeof error === "object" && error !== null
      ? (error as { code?: unknown; status?: unknown })
      : undefined
  return {
    status:
      response?.status ??
      (typeof directError?.status === "number"
        ? directError.status
        : undefined),
    code: response
      ? getApiErrorCodeFromValue(response.data)
      : getApiErrorCodeFromValue(directError?.code),
  }
}
